import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadReport, type ReportType, type ReportParams } from '@/utils/downloadReport';
import { useToastStore } from '@/store/useToastStore';
import { isApiError, translateApiError } from '@/errors';

/**
 * Reads the structured API error out of a failed blob request. With `responseType: 'blob'`
 * the error body arrives as a Blob too, so the usual parsing never sees its `code`.
 */
async function readBlobApiErrorMessage(error: unknown): Promise<string | null> {
  const data = (error as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const body: unknown = JSON.parse(await data.text());
    return isApiError(body) ? translateApiError(body) : null;
  } catch {
    // Not JSON (e.g. a proxy error page) — the caller falls back to the generic message.
    return null;
  }
}

export function useReportDownload() {
  const { t } = useTranslation('reports');
  const { addToast } = useToastStore();
  const [downloading, setDownloading] = useState(false);

  const download = async (
    type: ReportType,
    params: ReportParams,
    employeeId?: string,
  ) => {
    setDownloading(true);
    try {
      await downloadReport(type, params, employeeId);
      addToast(t('messages.downloadSuccess'), 'success');
    } catch (error) {
      // e.g. planning.reports.invalidMonth for a month before the hire date
      const apiMessage = await readBlobApiErrorMessage(error);
      addToast(apiMessage ?? t('errors.downloadFailed'), 'error');
    } finally {
      setDownloading(false);
    }
  };

  return { downloading, download };
}
